import { Tabs, Tooltip } from "antd"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router-dom"

import { ROLE_IDS } from "../../../../constants/roles.ts"
import { RoutesPaths } from "../../../../constants/routes.ts"
import { useAppSelector } from "../../../../hooks/use-app-selector.ts"
import { useTabsOpenInNewTab } from "../../../../hooks/use-tabs-open-in-new-tab.tsx"
import {
  selectActiveBranch,
  selectActiveBranchId,
  selectCompanyIsMultiBranch,
  selectIsOwner
} from "../../../Branches/redux/branches.slice.ts"
import { type PermissionFeatures } from "../../../SettingsPermissions/types/permissions.types.ts"
import { useAutomationSections } from "../../hooks/use-automation-sections.ts"
import {
  companyDefaultsSubtabsFor,
  useCanManageConfiguration
} from "./company-defaults-access.ts"

type TabItem = {
  key: string
  label: string
  tooltip?: string
  isVisible?: (permissions: PermissionFeatures) => boolean | undefined
  // Where clicking the tab navigates to, when different from `key` (a
  // grouping tab like "Company Defaults" has no page of its own — it
  // opens on its first member).
  navigateTo?: string
  // Extra pathname prefixes that also count as "this tab is active" —
  // again, for a grouping tab whose members live at their own routes.
  matchPrefixes?: string[]
}

// A tab stays active on its nested pages, so the longest matching key wins.
const resolveActiveKey = (items: TabItem[], pathname: string) =>
  items
    .filter(({ key, matchPrefixes }) =>
      [key, ...(matchPrefixes || [])].some(
        (prefix) => pathname === prefix || pathname?.startsWith(`${prefix}/`)
      )
    )
    .reduce((longest, item) => {
      return item.key.length > longest.length ? item.key : longest
    }, "")

interface SettingsLayoutTabsProps {
  permissions: PermissionFeatures
}

export default function SettingsLayoutTabs({
  permissions
}: SettingsLayoutTabsProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const pathname = useLocation()?.pathname
  const roleId = useAppSelector((state) => state.auth.userInfo?.role?.id)
  const automationSections = useAutomationSections(permissions)
  // A real, Django-provisioned GCC Owner (`invite_gcc_admin`) is seeded
  // with the legacy `role=TEAM_MEMBER` — `GccAssignment.is_owner` (here,
  // `selectIsOwner`) is the actual ownership signal, same as the route
  // guard (`BranchesOwnerGate`) already uses. Without this, such an owner
  // sees every other Settings tab but not Branches itself.
  const isOwner = useAppSelector(selectIsOwner)
  const activeBranchId = useAppSelector(selectActiveBranchId)
  const activeBranch = useAppSelector(selectActiveBranch)
  // The backend's `BranchViewSet.configuration` endpoint already lets this
  // branch's own manager (the `Team.manager` FK OR the `branch_manager`
  // permission label — `gcc_scope.user_manages_team`, carried here as
  // `Branch.is_manager`) read/edit its operational settings regardless of
  // the `settings_company_view`/`_edit` permission codes (which today only
  // `gcc_admin` carries) — but the tab itself was gated on exactly those
  // codes, so a manager with no other admin-shaped role could never even
  // reach the tab the backend already serves them.
  const isActiveBranchManager = !!activeBranch?.is_manager
  // A single-operation account's one Team is always the "active branch" by
  // id (`branches.slice.ts`'s first-load fallback — there is no "All
  // branches" view when there's nothing to aggregate), so `activeBranchId`
  // alone can never tell a branch workspace apart from a single-operation
  // company. `selectCompanyIsMultiBranch` is the real, company-level
  // signal: only once a company has actually activated a second branch
  // does "a specific branch is selected" mean anything distinct from "this
  // is just the company" — read company-wide (not `selectIsMultiBranch`,
  // which also requires the CALLER to be the owner), since a non-owning
  // admin viewing a real branch workspace must see the same label/hiding an
  // owner would, not Company HQ's.
  const isMultiBranch = useAppSelector(selectCompanyIsMultiBranch)
  // "Company & System" means a different record depending on the active
  // workspace (see `SettingsCompanyPage`) — the tab label says so, rather
  // than reading the same as it does for Company HQ.
  const isBranchWorkspace = isMultiBranch && !!activeBranchId

  const canManageConfiguration =
    roleId === ROLE_IDS.ADMIN_GCC ||
    roleId === ROLE_IDS.ADMIN_PLATFORM ||
    isOwner
  // Branch create/archive/restore (`apps.user.branch_service`) is HQ-only —
  // a specific branch workspace has no equivalent screen, and neither does
  // a single-operation company (never multi-branch, so this is always
  // false for it, with no "Add branch" entry anywhere in Settings either).
  const companyDefaultsSubtabs = companyDefaultsSubtabsFor(
    permissions,
    useCanManageConfiguration()
  )
  const showBranchesTab =
    canManageConfiguration && isMultiBranch && !activeBranchId

  // The whole permission tree, not just its `settings` branch: Lead Life Cycle
  // is gated on a leads right, so narrowing the prop would put that tab out of
  // reach of its own permission.
  const allTabs: TabItem[] = [
    {
      key: RoutesPaths.settings.profile,
      label: "Profile & User"
    },
    {
      key: RoutesPaths.settings.company,
      label: isBranchWorkspace ? "Branch & System" : "Company & System",
      isVisible: (p) =>
        p?.settings?.company?.settings_company_view ||
        (isBranchWorkspace && isActiveBranchManager)
    },
    // Configurations and Templates are grouped under one "Company Defaults"
    // tab — the authoritative, reusable baseline every branch inherits from
    // or overrides — rather than separate top-level tabs. Each still lives at
    // its own pre-existing route; this tab opens on the first and the group's
    // own CompanyDefaultsSubTabs strip (rendered by those pages) switches
    // between them. Visible in every mode (single-operation, Company HQ, and
    // a branch workspace) — it is the content underneath that re-scopes
    // itself to "Defaults for [branch]" inside a branch workspace, never the
    // tab. Hiding the tab there was this session's own regression: it left a
    // branch workspace with no Company Defaults tab at all rather than a
    // branch-scoped one.
    {
      key: "company-defaults",
      label: "Company Defaults",
      navigateTo: companyDefaultsSubtabs[0]?.key,
      matchPrefixes: [
        RoutesPaths.settings.configuration,
        RoutesPaths.settings.templates
      ],
      isVisible: () => companyDefaultsSubtabs.length > 0
    },
    {
      key: RoutesPaths.settings.permissions,
      label: "Permissions",
      isVisible: (p) => p?.settings?.permissions?.settings_permissions_view
    },
    {
      key: RoutesPaths.settings.notifications,
      label: "Notifications"
    },
    {
      key: RoutesPaths.settings.smsUsage,
      label: t("smsUsage.tab"),
      isVisible: (p) =>
        roleId === ROLE_IDS.ADMIN_PLATFORM ||
        p?.settings?.company?.settings_company_view
    },
    {
      key: RoutesPaths.settings.branches,
      label: "Branches",
      isVisible: () => showBranchesTab
    },
    // Communication, Status and Required Information automation, grouped the
    // same way; it opens on whichever of them the user may see first.
    {
      key: RoutesPaths.settings.automation,
      label: t("settingsAutomation.tab"),
      navigateTo: automationSections[0]?.route,
      matchPrefixes: automationSections.map(({ route }) => route),
      isVisible: () => automationSections.length > 0
    }
  ]

  const items = allTabs?.filter(
    ({ isVisible }) => !isVisible || !!isVisible(permissions)
  )

  const activeKey = resolveActiveKey(items, pathname)
  const newTab = useTabsOpenInNewTab((key) => {
    const target = items?.find((item) => item.key === key)
    return target ? target.navigateTo || target.key : undefined
  })

  return (
    <div className="border-b border-gray-200" {...newTab.handlers}>
      {newTab.contextMenu}
      <Tabs
        activeKey={activeKey || items?.[0]?.key}
        items={items?.map(({ key, label, tooltip }) => ({
          key,
          label: tooltip ? (
            <Tooltip title={tooltip}>
              <span>{label}</span>
            </Tooltip>
          ) : (
            label
          )
        }))}
        onChange={(key) => {
          const target = items?.find((item) => item.key === key)
          navigate(target?.navigateTo || key)
        }}
        className="[&_.ant-tabs-nav]:mb-0 [&_.ant-tabs-tab]:py-1"
      />
    </div>
  )
}
